import { UserGroup } from "~/api/API";
import { AuthUserGroup } from "~/types/auth";

/**
 * Bridge between Cognito group strings (PascalCase, e.g. "Admin") and the
 * GraphQL UserGroup enum stored on `Skill.roles` (SCREAMING_SNAKE, e.g. "ADMIN").
 * Mirrors `_ACTOR_ROLE_TO_SKILL_USER_GROUP` in services/agent_chat/skills.py;
 * keep them in lock-step when new roles are added.
 */
const AUTH_TO_USER_GROUP: Record<AuthUserGroup, UserGroup | null> = {
  [AuthUserGroup.ADMIN]: UserGroup.ADMIN,
  [AuthUserGroup.DOCTOR]: UserGroup.DOCTOR,
  [AuthUserGroup.MEDICAL_ASSISTANT]: UserGroup.MEDICAL_ASSISTANT,
  [AuthUserGroup.HISTORIAN]: UserGroup.HISTORIAN,
  [AuthUserGroup.UNAUTHORIZED]: null,
};

/**
 * Returns true when the user may invoke the given skill.
 *
 * Open (no roles set) → visible to anyone with chatbot access.
 * Otherwise → user's role must intersect `skill.roles`.
 *
 * The backend re-validates this on every invocation via SkillService.load,
 * so the frontend filter is defence in depth + UX (hiding from the menu).
 */
export function actorCanInvoke(
  skill: { roles?: UserGroup[] | null },
  authUserGroup: AuthUserGroup | null | undefined,
): boolean {
  if (!skill.roles || skill.roles.length === 0) return true;
  if (!authUserGroup) return false;
  const mapped = AUTH_TO_USER_GROUP[authUserGroup];
  if (mapped === null) return false;
  return skill.roles.includes(mapped);
}
